import { ButtonHTMLAttributes, forwardRef } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-cherry-600 text-white hover:bg-cherry-700 active:bg-cherry-800 shadow-cherry hover:shadow-cherry-lg active:scale-[0.98] transform hover:scale-[1.02]',
  secondary:
    'bg-white text-charcoal-900 border-2 border-charcoal-200 hover:border-cherry-600 hover:text-cherry-600 active:bg-cherry-50',
  ghost:
    'text-charcoal-600 hover:bg-charcoal-50 hover:text-charcoal-900 active:bg-charcoal-100',
  danger:
    'bg-score-poor text-white hover:bg-score-poor/90 active:bg-score-poor/80',
};

// Minimum touch target: 44px height
const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-4 py-2.5 text-sm min-h-11',
  md: 'px-5 py-3 text-base min-h-11',
  lg: 'px-8 py-4 text-lg min-h-[52px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      fullWidth = false,
      disabled,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={`
          inline-flex items-center justify-center gap-2
          font-semibold rounded-xl transition-all duration-200
          disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
          focus:outline-none focus:ring-2 focus:ring-cherry-600 focus:ring-offset-2
          ${variantStyles[variant]}
          ${sizeStyles[size]}
          ${fullWidth ? 'w-full' : ''}
          ${className}
        `}
        {...props}
      >
        {isLoading && (
          <svg
            className="animate-spin h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
